import { DotLottiePlayer } from '@dotlottie/react-player';
import { Typography } from 'antd';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent as NoTxn } from '../../../Assets/noTxn.svg';
import { ReactComponent as Down } from '../../../Assets/yesbank/down-arrow.svg';

import { AppContext } from '../../../App';
import { getLottiePath } from '../../../Utils/assetUtils';
import { getTransactions } from '../../../Services/PFMClient/DashboardService';
import { groupByTransactionsCredit, mergeObjects } from '../../../Utils/utils';
import Transaction from '../Common/Transaction';
import styles from './index.module.css';

// Kept constant across every page — Spring's Pageable computes offset as
// page * size, so changing size mid-sequence would skip or duplicate records.
const PAGE_SIZE = 10;

const AllTransactions: React.FC<{
  selectedAccounts: any;
  isViewTransationVisible?: boolean;
  children: React.ReactNode;
}> = ({ selectedAccounts, isViewTransationVisible = false, children }) => {
  const [selectedAccountsFilter, setSelectedAccountFilter] = useState<any>();

  // Track the last account id to avoid re-fetching when accountsInfo recomputes
  // with the same account (e.g., due to polling/forceRender updates).
  const prevAccountIdRef = useRef<any>(null);

  useEffect(() => {
    const newId = selectedAccounts?.id;
    if (newId !== prevAccountIdRef.current) {
      prevAccountIdRef.current = newId;
      // Real account switch — reset accumulated transactions and pagination
      setAllTransactions({});
      setPage(0);
      setHasMore(true);
      isFetchingMoreRef.current = false;
      setSelectedAccountFilter(selectedAccounts);
    }
  }, [selectedAccounts]);

  const [allTransactions, setAllTransactions] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const { analyticsStatus }: any = useContext(AppContext);
  const [apiCallComplted, setApiCallCompleted] = useState(false);
  const [page, setPage] = useState(0);

  const [hasMore, setHasMore] = useState(true);
  const isFetchingMoreRef = useRef(false);

  useEffect(() => {
    if (apiCallComplted) setLoading(false);
  }, [allTransactions]);

  const getTransactionApiCall = () => {
    setApiCallCompleted(false);
    setLoading(true);

    if (selectedAccounts?.isOtherFiType) {
      setAllTransactions((prev: any) =>
        mergeObjects(
          prev,
          groupByTransactionsCredit(selectedAccounts?.transaction)
        )
      );
      setLoading(false);
      setApiCallCompleted(true);
    } else {
      getTransactions(
        {
          accountIdList: [selectedAccountsFilter?.id],
        },
        { page, pageSize: PAGE_SIZE }
      )
        .then((res) => res?.data)
        .then((res) => {
          if (res?.totalPages <= page + 1) {
            setHasMore(false);
          }
          setAllTransactions((prev: any) =>
            mergeObjects(
              prev,
              groupByTransactionsCredit(res?.elements)
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setApiCallCompleted(true);
          isFetchingMoreRef.current = false;
        });
    }
  };

  useEffect(() => {
    if (analyticsStatus === 'PENDING') {
      setLoading(false);
    } else if (
      selectedAccountsFilter &&
      selectedAccountsFilter?.id !== '' &&
      hasMore
    ) {
      getTransactionApiCall();
    }
  }, [selectedAccountsFilter, page, hasMore]);

  const showItems = () => {
    return Object.keys(allTransactions).length > 0 ? (
      <>
        {Object.keys(allTransactions).map((txn: any, i: number) => {
          const txnArray = allTransactions[txn];

          return (
            <Fragment key={txn}>
              <Typography.Text
                style={{
                  color: '#052F5FB2',
                  fontSize: 10,
                  fontWeight: 700,
                  marginTop: 24,
                }}
              >
                {txn.toUpperCase()}
              </Typography.Text>

              <div
                style={{
                  padding: '0px',
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: 8,
                  gap: 10,
                }}
              >
                {txnArray?.map((transaction: any, index: number) => {
                  return (
                    <Transaction
                      key={
                        transaction?.id
                          ? transaction.id
                          : `${transaction?.category}-${index}`
                      }
                      className={styles.txnCard}
                      transaction={transaction}
                      transactionImageBGProp={styles.transactionImageBG}
                      iconSize={styles.iconSize}
                    />
                  );
                })}
              </div>
            </Fragment>
          );
        })}

        {loading ? (
          <DotLottiePlayer
            speed={2}
            id='loadingLottie'
            src={getLottiePath('loading.lottie')}
            autoplay
            loop
            style={{
              height: '80px',
            }}
          />
        ) : (
          <></>
        )}
      </>
    ) : loading ? (
      <DotLottiePlayer
        speed={2}
        id='loadingLottie'
        src={getLottiePath('loading.lottie')}
        autoplay
        loop
        style={{
          height: '130px',
        }}
      />
    ) : (
      <div className={styles.emptyContainer}>
        <NoTxn />
        <span className={styles.emptyText}>No transaction found</span>
      </div>
    );
  };

  return (
    <div className={`${styles.transactionContainer} transactionContainer`}>
      <div
        style={{
          margin: '8px 0 0 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <Typography.Text
          style={{ color: 'black' }}
          className={`fontFamilyHeader `}
        >
          Transactions
        </Typography.Text>
        <Typography.Text
          style={{
            opacity: 0.7,
            fontSize: 12,
            color: 'black',
            fontWeight: 600,
          }}
        >
          Here is list of recent transactions
        </Typography.Text>
      </div>

      <div className={`transactionSection`}>
        {showItems()}

        {!selectedAccounts?.isOtherFiType &&
        isViewTransationVisible &&
        !loading &&
        Object.keys(allTransactions).length > 0 &&
        hasMore ? (
          <Typography.Text
            style={{
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              justifyContent: 'center',
              marginTop: 8,
              color: 'var(--more-less-faqs)',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (isFetchingMoreRef.current) return;
              isFetchingMoreRef.current = true;
              setPage((page) => page + 1);
            }}
          >
            <Down style={{ marginRight: 4 }} /> View More
          </Typography.Text>
        ) : (
          <></>
        )}
      </div>

      {children}
    </div>
  );
};

export default AllTransactions;
